import React, { useCallback, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { ArrowDownUp } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useMaterials, type DatabaseMaterial, type MaterialCategory } from '@/hooks/useMaterials';
import { useHiddenMaterials } from '@/hooks/useHiddenMaterials';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { groupByFormat, type MaterialGroup } from './dedupByFormat';
import { formatNumber } from '@/utils/format';
import { profileCoating, sheetThicknessLabel } from '@/lib/materials/taxonomy';
import { facetsFor, type Facet } from '@/lib/materials/facets';

const CATEGORY_TABS: { value: MaterialCategory | 'all'; label: string }[] = [
  { value: 'all', label: 'Tutti' },
  { value: 'board', label: 'Lastre' },
  { value: 'insulation', label: 'Isolanti' },
  { value: 'structure_frame', label: 'Montanti' },
  { value: 'structure_guide', label: 'Guide' },
  { value: 'screw', label: 'Viti' },
  { value: 'accessory', label: 'Accessori' },
  { value: 'finish', label: 'Finiture' },
  { value: 'ceiling_tile', label: 'Pannelli soff.' },
];

type SortBy = 'relevance' | 'price_asc' | 'price_desc' | 'thickness_asc' | 'thickness_desc' | 'name';

const SORT_OPTIONS: { value: SortBy; label: string }[] = [
  { value: 'relevance',     label: 'Rilevanza (default)' },
  { value: 'price_asc',     label: 'Prezzo crescente' },
  { value: 'price_desc',    label: 'Prezzo decrescente' },
  { value: 'thickness_asc', label: 'Spessore crescente' },
  { value: 'thickness_desc',label: 'Spessore decrescente' },
  { value: 'name',          label: 'Nome A-Z' },
];


interface MaterialPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialCategory?: MaterialCategory | 'all';
  onSelect: (material: DatabaseMaterial) => void;
}

const MaterialPickerDialog: React.FC<MaterialPickerDialogProps> = ({
  open,
  onOpenChange,
  initialCategory = 'all',
  onSelect,
}) => {
  const { data: allMaterials = [], isLoading } = useMaterials();
  const { hiddenSet } = useHiddenMaterials();

  // Lista fornitori NON di sistema (esclude DryCore set generico)
  const { data: visibleSuppliers = [] } = useQuery({
    queryKey: ['suppliers-visible'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('suppliers')
        .select('name, slug, is_system')
        .order('name');
      if (error) throw error;
      return (data ?? []).filter((s: any) => !s.is_system).map((s: any) => s.name as string);
    },
    staleTime: 1000 * 60 * 60,
  });

  const [activeCategory, setActiveCategory] = useState<MaterialCategory | 'all'>(initialCategory);
  const [activeSupplier, setActiveSupplier] = useState<string | 'all'>('all');
  // Origine del materiale: catalogo condiviso, personalizzati dalla mia azienda
  // (prezzo/tempo miei) o creati dalla mia azienda.
  const [activeScope, setActiveScope] = useState<'all' | 'custom' | 'mine' | 'catalog'>('all');
  /** Faccetta → valori selezionati. Vuoto o assente = nessun filtro. */
  const [activeFacets, setActiveFacets] = useState<Record<string, string[]>>({});
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<SortBy>('relevance');

  // Sync iniziale
  React.useEffect(() => {
    if (open) {
      setActiveCategory(initialCategory);
      setActiveFacets({});
      setSearch('');
      setSortBy('relevance');
    }
  }, [open, initialCategory]);

  /** Faccette della categoria corrente. In "Tutti" non se ne mostra nessuna. */
  const facets = useMemo<Facet[]>(() => facetsFor(activeCategory), [activeCategory]);

  const toggleFacetValue = (facetKey: string, value: string) => {
    setActiveFacets(prev => {
      const current = prev[facetKey] ?? [];
      const next = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      const out = { ...prev };
      if (next.length === 0) delete out[facetKey];
      else out[facetKey] = next;
      return out;
    });
  };

  // Materiali "visibili" = non di sistema e non nascosti dall'org. Per filtrare
  // per supplier_name uso l'array dei fornitori non-system.
  const baseMaterials = useMemo(() => {
    let list = allMaterials.filter(m => !hiddenSet.has(m.id));
    if (visibleSuppliers.length > 0) {
      list = list.filter(m => !m.supplier || visibleSuppliers.includes(m.supplier));
    }
    // Filtro origine: personalizzati (prezzo/tempo fissati dall'org), materiali
    // creati dall'org, oppure solo catalogo condiviso.
    if (activeScope === 'custom') {
      list = list.filter(m => m.has_price_override || m.has_time_override);
    } else if (activeScope === 'mine') {
      list = list.filter(m => !!m.organization_id);
    } else if (activeScope === 'catalog') {
      list = list.filter(m => !m.organization_id);
    }
    return list;
  }, [allMaterials, visibleSuppliers, hiddenSet, activeScope]);

  // Dedup per formato: 4 codici "Wallboard 10" diversi solo per length → 1 sola card.
  const groupedBase = useMemo<MaterialGroup[]>(
    () => groupByFormat(baseMaterials),
    [baseMaterials],
  );

  /**
   * Applica i filtri saltandone uno.
   *
   * Serve ai CONTEGGI: il numero accanto a un chip deve dire quanti materiali
   * resterebbero SCEGLIENDO QUEL CHIP, quindi va calcolato con tutti gli altri
   * filtri attivi ma senza quello. Prima i conteggi di categoria e fornitore
   * ignoravano del tutto il chip tipologia e mostravano numeri che non
   * tornavano con la lista sotto.
   */
  const applyFilters = useCallback((
    arr: MaterialGroup[],
    skip?: { category?: boolean; supplier?: boolean; facetKey?: string },
  ) => {
    let out = arr;
    if (!skip?.category && activeCategory !== 'all') {
      out = out.filter(g => g.representative.category === activeCategory);
    }
    if (!skip?.supplier && activeSupplier !== 'all') {
      out = out.filter(g => g.representative.supplier === activeSupplier);
    }
    for (const facet of facets) {
      if (facet.key === skip?.facetKey) continue;
      const selected = activeFacets[facet.key];
      if (!selected || selected.length === 0) continue;
      out = out.filter(g => facet.valuesOf(g.representative).some(v => selected.includes(v)));
    }
    return out;
  }, [activeCategory, activeSupplier, facets, activeFacets]);

  // Pool per i conteggi dei FORNITORI: tutto tranne il fornitore stesso.
  const poolByCategory = useMemo(
    () => applyFilters(groupedBase, { supplier: true }),
    [groupedBase, applyFilters],
  );

  // Pool per i conteggi delle CATEGORIE: tutto tranne la categoria stessa.
  // NB: le faccette sono per-categoria, quindi qui non si applicano.
  const poolBySupplier = useMemo(() => {
    if (activeSupplier === 'all') return groupedBase;
    return groupedBase.filter(g => g.representative.supplier === activeSupplier);
  }, [groupedBase, activeSupplier]);

  const filtered = useMemo(() => applyFilters(groupedBase), [groupedBase, applyFilters]);

  // Ordinamento (cmdk applica già rilevanza per fuzzy sopra al sort esplicito)
  const sorted = useMemo<MaterialGroup[]>(() => {
    if (sortBy === 'relevance') return filtered;
    const arr = [...filtered];
    const priceOf = (g: MaterialGroup) => Number(g.representative.net_price ?? g.representative.unit_price ?? 0);
    const thickOf = (g: MaterialGroup) => Number(g.representative.thickness ?? 0);
    arr.sort((a, b) => {
      switch (sortBy) {
        case 'price_asc':      return priceOf(a) - priceOf(b);
        case 'price_desc':     return priceOf(b) - priceOf(a);
        case 'thickness_asc':  return thickOf(a) - thickOf(b);
        case 'thickness_desc': return thickOf(b) - thickOf(a);
        case 'name':           return (a.representative.name ?? '').localeCompare(b.representative.name ?? '');
        default: return 0;
      }
    });
    return arr;
  }, [filtered, sortBy]);

  const handleSelect = (material: DatabaseMaterial) => {
    onSelect(material);
    onOpenChange(false);
    setSearch('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl p-0 gap-0 max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader className="px-4 py-3 border-b">
          <DialogTitle>Seleziona materiale</DialogTitle>
          <DialogDescription className="text-xs">
            Cerca per codice, nome o fornitore. Filtra per categoria, fornitore e tipologia.
          </DialogDescription>
        </DialogHeader>

        {/* Tab categoria con count dinamici sul pool del supplier attivo */}
        <div className="flex flex-wrap gap-1 px-4 py-2 border-b">
          {CATEGORY_TABS.map(tab => {
            const count = tab.value === 'all'
              ? poolBySupplier.length
              : poolBySupplier.filter(g => g.representative.category === tab.value).length;
            return (
              <Button
                key={tab.value}
                size="sm"
                variant={activeCategory === tab.value ? 'default' : 'outline'}
                // Cambiando categoria le faccette non hanno piu' senso: si azzerano.
                onClick={() => { setActiveCategory(tab.value); setActiveFacets({}); }}
                className="h-7 text-xs"
                disabled={count === 0 && tab.value !== 'all'}
              >
                {tab.label}
                <span className="ml-1 opacity-70">{count}</span>
              </Button>
            );
          })}
        </div>

        {/* Faccette della categoria attiva: una riga per faccetta, scelta multipla.
            I conteggi sono calcolati escludendo la faccetta stessa, cosi' dicono
            quanti materiali resterebbero cliccando quel chip. */}
        {facets.length > 0 && (
          <div className="border-b bg-muted/30">
            {facets.map(facet => {
              const selected = activeFacets[facet.key] ?? [];
              const pool = applyFilters(groupedBase, { facetKey: facet.key });
              const visible = facet.options
                .map(opt => ({
                  opt,
                  count: pool.filter(g => facet.valuesOf(g.representative).includes(opt.value)).length,
                }))
                .filter(o => o.count > 0);
              if (visible.length === 0) return null;
              return (
                <div key={facet.key} className="flex flex-wrap gap-1 px-4 py-1.5 items-center">
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wide mr-2 w-20 shrink-0">
                    {facet.label}
                  </span>
                  {selected.length > 0 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setActiveFacets(prev => {
                        const out = { ...prev };
                        delete out[facet.key];
                        return out;
                      })}
                      className="h-6 text-[11px] px-2 text-muted-foreground"
                      title={`Azzera ${facet.label.toLowerCase()}`}
                    >
                      ✕
                    </Button>
                  )}
                  {visible.map(({ opt, count }) => (
                    <Button
                      key={opt.value}
                      size="sm"
                      variant={selected.includes(opt.value) ? 'secondary' : 'ghost'}
                      onClick={() => toggleFacetValue(facet.key, opt.value)}
                      className="h-6 text-[11px] px-2"
                    >
                      {opt.label} <span className="ml-1 opacity-70">{count}</span>
                    </Button>
                  ))}
                </div>
              );
            })}
          </div>
        )}

        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Filtri laterali fornitore con count dinamici sul pool della categoria attiva */}
          <div className="w-52 border-r p-3 hidden md:block overflow-auto">
            {/* Origine materiale: aiuta a capire se sto usando il catalogo o i miei prezzi */}
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Origine</div>
            <div className="space-y-1 mb-3">
              {([
                { v: 'all', label: 'Tutti' },
                { v: 'custom', label: '✏️ Con i miei prezzi' },
                { v: 'mine', label: 'Miei materiali' },
                { v: 'catalog', label: 'Solo catalogo' },
              ] as const).map(opt => (
                <button
                  key={opt.v}
                  className={`w-full text-left text-xs px-2 py-1 rounded ${activeScope === opt.v ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted'}`}
                  onClick={() => setActiveScope(opt.v)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Fornitore</div>
            <div className="space-y-1">
              <button
                className={`w-full text-left text-xs px-2 py-1 rounded ${activeSupplier === 'all' ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted'}`}
                onClick={() => setActiveSupplier('all')}
              >
                Tutti <span className="opacity-70">({poolByCategory.length})</span>
              </button>
              {visibleSuppliers.map(s => {
                const count = poolByCategory.filter(g => g.representative.supplier === s).length;
                if (count === 0) return null; // nasconde se 0 nella categoria attiva
                return (
                  <button
                    key={s}
                    className={`w-full text-left text-xs px-2 py-1 rounded ${activeSupplier === s ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted'}`}
                    onClick={() => setActiveSupplier(s)}
                  >
                    {s} <span className="opacity-70">({count})</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Search + lista + sort */}
          <div className="flex-1 min-w-0 flex flex-col">
            <Command shouldFilter={true} className="flex-1 flex flex-col">
              <div className="flex items-center border-b">
                <CommandInput
                  placeholder="🔍 Cerca per codice, nome, fornitore…"
                  value={search}
                  onValueChange={setSearch}
                  className="h-10 flex-1 border-0"
                />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-9 mx-2 gap-1.5 text-xs">
                      <ArrowDownUp className="h-3.5 w-3.5" />
                      {SORT_OPTIONS.find(s => s.value === sortBy)?.label.split(' ')[0]}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {SORT_OPTIONS.map(opt => (
                      <DropdownMenuItem
                        key={opt.value}
                        onClick={() => setSortBy(opt.value)}
                        className={sortBy === opt.value ? 'bg-primary/10' : ''}
                      >
                        {opt.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <CommandList className="flex-1 overflow-auto">
                {isLoading && <div className="p-4 text-center text-sm text-muted-foreground">Caricamento…</div>}
                <CommandEmpty>Nessun materiale trovato.</CommandEmpty>
                <CommandGroup>
                  {sorted.map(group => {
                    const m = group.representative;
                    const netPrice = m.net_price ?? m.unit_price ?? 0;
                    const listPrice = m.list_price ?? netPrice;
                    const isDiscounted = listPrice > netPrice + 0.001;
                    // searchValue include i codici di TUTTE le varianti, così la ricerca per
                    // codice (es. "5200615968") matcha comunque anche se mostriamo il representative.
                    const allCodes = group.variants.map(v => v.code).join(' ');
                    const searchValue = `${allCodes} ${m.name} ${m.supplier} ${m.material_type ?? ''} ${(m as any).family_code ?? ''}`;
                    return (
                      <CommandItem
                        key={m.id}
                        value={searchValue}
                        onSelect={() => handleSelect(m)}
                        className="flex items-start gap-3 cursor-pointer py-2"
                      >
                        <div
                          className="w-10 h-10 rounded border border-zinc-200 shrink-0"
                          style={{ backgroundColor: swatchColor(m) }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">{m.name}</div>
                          <div className="text-[11px] text-muted-foreground flex items-center gap-1.5 flex-wrap">
                            <span>{m.supplier}</span>
                            {m.material_type && <span>· {m.material_type}</span>}
                            {m.thickness != null && <span>· {m.thickness} mm</span>}
                            {(m.category === 'structure_frame' || m.category === 'structure_guide') && (
                              <>
                                {m.width != null && <span>· sez. {m.width} mm</span>}
                                {/* Lamierino e rivestimento: sono i due criteri con
                                    cui si sceglie davvero un profilo, e prima non
                                    comparivano affatto nella riga. */}
                                {m.sheet_thickness != null && (
                                  <span>· {sheetThicknessLabel(m.sheet_thickness)}</span>
                                )}
                                {profileCoating(m) === 'zinco_magnesio' && (
                                  <span className="text-emerald-700">· Zn-Mg</span>
                                )}
                              </>
                            )}
                          </div>
                          {group.variants.length > 1 && group.formats.length > 0 && (
                            <div className="text-[10px] text-muted-foreground/80 mt-0.5">
                              <span className="font-medium">Formati:</span> {group.formats.join(' · ')}
                              <span className="ml-1 opacity-60">({group.variants.length} cod.)</span>
                            </div>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-medium text-sm text-green-700">
                            €{formatNumber(netPrice)}/{m.unit}
                          </div>
                          {isDiscounted && (
                            <div className="text-[10px] text-muted-foreground line-through">
                              €{formatNumber(listPrice)}
                            </div>
                          )}
                        </div>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

function swatchColor(m: DatabaseMaterial): string {
  const mt = (m.material_type ?? '').toLowerCase();
  const cat = m.category;
  if (cat === 'screw') return '#1F2937';
  if (cat === 'structure_frame' || cat === 'structure_guide') return '#9CA3AF';
  if (cat === 'finish') return '#E5E7EB';
  if (mt.includes('idro') || mt.includes('hydro')) return '#CEE3F0';
  if (mt.includes('ignifug') || mt.includes('fire')) return '#F4D3CF';
  if (mt.includes('cemento') || mt.includes('aquaroc')) return '#C9C2B6';
  if (mt.includes('glasroc')) return '#D8E8DA';
  if (mt.includes('lana_roccia')) return '#FFE08A';
  if (mt.includes('lana_vetro')) return '#FFF3B8';
  if (mt.includes('eps') || mt.includes('xps')) return '#F0F0F0';
  if (mt.includes('pir')) return '#FFD6A5';
  if (cat === 'board' || cat === 'ceiling_tile') return '#F5F1E8';
  return '#FFFFFF';
}

export default MaterialPickerDialog;
